
import { Component, Inject, Ref, Vue, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { namespace } from 'vuex-class';

import AdUnit from '@/components/AdUnit.vue';
import BuyBox /* webpackChunkName: "buybox" */ from '@/components/buybox/BuyBox.vue';
import PromotedBuyBox /* webpackChunkName: "promoted-buybox" */ from '@/components/buybox/PromotedBuyBox.vue';
import ArticleBlock from '@/components/details/editorial/ArticleBlock.vue';
import ProductionNewsBlock from '@/components/details/editorial/ProductionNewsBlock.vue';
import InfoBox from '@/components/details/InfoBox.vue';
import TitleBackdrops from '@/components/details/TitleBackdrops.vue';
import TitleBlock from '@/components/details/TitleBlock.vue';
import TitleInfo from '@/components/details/TitleInfo.vue';
import GuideList from '@/components/editorial/GuideList.vue';
import LazyClientShow from '@/components/LazyClientShow.vue';
import LoginButton from '@/components/LoginButton.vue';
import { AvailabilityBadgeType } from '@/components/poster/types/availability-badge';
import QuickActionBar from '@/components/QuickActionBar.vue';
import SimilarTitlesGraphql from '@/components/SimilarTitlesGraphql.vue';

import SimilarTitlesProvider from '@/helpers/providers/similar-titles-provider';

import { diffInDays, diffInMilliseconds } from '@/helpers/date-helper';
import { showFreemium } from '@/helpers/iap-helper';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';

import { resetPermanentAudiences, setPermanentAudiences, TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { TrafficHelper } from '@/helpers/traffic-helper';

import { getThumborUrl } from '@/filters/thumbor';
import { QuickActionBarProps } from '@/interfaces/components/QuickActionBar';
import Genre from '@/interfaces/genre';
import { TitleListName } from '@/interfaces/responses/title-list';
import { TitleContentType } from '@/interfaces/snowplow/title-context';
import { TitleDetails, TitleDetailsNode } from '@/interfaces/title-details-graphql';

import { ObjectType, Platform, TitleFilter, TitleListType } from '@/@types/graphql-types';

import { UserSettings } from '@/stores/user.store';

import { PopularTitle } from '@/interfaces/titles';
import {
	GetTitleDetailArticleDocument,
	GetTitleDetailArticleQuery,
	GetTitleDetailArticleQueryVariables,
	TitleDetailArticleFragment,
} from '@/pages/graphql/queries/GetTitleDetailArticle.query';
import {
	GetUrlTitleDetailsDocument,
	GetUrlTitleDetailsQuery,
	GetUrlTitleDetailsQueryVariables,
} from '@/pages/graphql/queries/GetUrlTitleDetails.query';

import TitleHelper from '@/helpers/title-helper';

import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import type {
	SnowplowContext,
	SnowplowModuleContext,
	SnowplowTitleContextGraphqlPayload,
} from '@/helpers/tracking/providers/snowplow-contexts';
import type { Content } from '@/interfaces/components/content';
import { SponsoredAdFragmentFragment } from './graphql/queries/GetPopularTitles.query';

const AvailabilityBadge = () => import('@/components/poster/AvailabilityBadge.vue');
const EpisodesList = () => import('@/components/EpisodesList.vue');
const HorizontalTitleListGraphql = () => import('@/components/timeline/HorizontalTitleListGraphql.vue');
const PosterGraphql = () => import('@/components/poster/PosterGraphql.vue');
const SeasonsList = () => import('@/components/SeasonsList.vue');
const SpinningText = () => import('@/components/details/SpinningText.vue');
const TitleClips = () => import('@/components/details/TitleClips.vue');
const TitleCredits = () => import('@/components/details/TitleCredits.vue');
const TitleListProvider = () => import('@/helpers/providers/title-list-provider');
const TitleStreamingChart = () => import('@/components/details/TitleStreamingChart.vue');
const TitleSynopsis = () => import('@/components/details/TitleSynopsis.vue');
const UpcomingContent = () => import('@/components/upcoming/UpcomingContent.vue');

const App = namespace('app');
const Constant = namespace('constant');
const Experiment = namespace('experiment');
const Language = namespace('language');
const Routing = namespace('routing');
const TitleDetailsStore = namespace('titleDetails');
const User = namespace('user');

@Component({
	name: 'TitleDetail',
	components: {
		AdUnit,
		ArticleBlock,
		AvailabilityBadge,
		BuyBox,
		EpisodesList,
		GuideList,
		HorizontalTitleListGraphql,
		InfoBox,
		LazyClientShow,
		LoginButton,
		PosterGraphql,
		ProductionNewsBlock,
		PromotedBuyBox,
		QuickActionBar,
		SeasonsList,
		SimilarTitlesGraphql,
		SimilarTitlesProvider,
		SpinningText,
		TitleBackdrops,
		TitleBlock,
		TitleClips,
		TitleCredits,
		TitleInfo,
		TitleListProvider,
		TitleStreamingChart,
		TitleSynopsis,
		UpcomingContent,
	},
	metaInfo() {
		return this.head;
	},
	apollo: {
		titleDetails: {
			query: GetUrlTitleDetailsDocument,
			variables(): GetUrlTitleDetailsQueryVariables {
				return this.queryVariables;
			},
			update({ urlV2: data }: GetUrlTitleDetailsQuery) {
				const title = data.node as AssertByTypename<typeof data.node, 'Movie' | 'Show' | 'Season'>;

				this.$store.dispatch('meta/setTitleJsonLD', { title });
				if (title.__typename !== 'Season') {
					setPermanentAudiences(title.permanentAudiences);
				}
				return data;
			},
			error(e) {
				console.log(e);
				process.client ? this.showErrorToast() : null;
			},
			skip() {
				return this.activeRoute?.meta?.tab !== 'detail';
			},
		},
		article: {
			query: GetTitleDetailArticleDocument,
			variables(): GetTitleDetailArticleQueryVariables {
				return {
					fullPath: this.$route.fullPath,
					language: this.language,
					country: this.country,
				};
			},
			update({ urlV2: data }: GetTitleDetailArticleQuery) {
				type Node = AssertByTypename<typeof data.node, 'Movie' | 'Show' | 'Season'>;
				return (data.node as Node).content?.article?.content ?? null;
			},
			errorPolicy: 'ignore',
			skip() {
				return this.activeRoute?.meta?.tab !== 'detail';
			},
		},
	},
})
export default class TitleDetail extends Vue {
	TitleListType = TitleListType;

	activeClipId: string | null = null;
	episodeMaxLimit: number | null = 20;
	location = 'TitleDetail';
	titleDetails: TitleDetails = {} as TitleDetails;
	article: TitleDetailArticleFragment['content'] | null = null;

	// Used to show skeleton while buybox above the fold is loading to prevent CLS.
	// Could be used for similar cases with other components
	isReadyToRender = true;

	@Ref('content') readonly content!: Content;

	@App.Getter isSeoUser: boolean;

	@Constant.Getter genresByShortName: Record<string, Genre>;

	@Language.Getter country: string;
	@Language.Getter language: string;

	@TitleDetailsStore.Getter refferalModuleContext: SnowplowModuleContext | null;
	@TitleDetailsStore.Getter sponsoredAd: SponsoredAdFragmentFragment;
	@TitleDetailsStore.Action setSponsoredRecommendation: (sponsoredAd: SponsoredAdFragmentFragment | null) => void;
	@TitleDetailsStore.Action setTitleContext: (payload: SnowplowTitleContextGraphqlPayload) => void;
	@TitleDetailsStore.Action clearTitleContext: () => void;

	@Routing.State activeRoute: Route;
	@Routing.State lastRoute: Route;

	@User.Getter isLoggedIn: (providerName?: string) => boolean;
	@User.Getter isPremium: boolean;
	@User.State settings: UserSettings;

	@Experiment.Getter activeVariantsWithControlGroup: Record<string, string>;

	get showNewSeasonList() {
		return this.country === 'GB';
	}

	get head() {
		return {
			meta: [
				{
					vmid: 'og:image',
					property: 'og:image',
					content: getThumborUrl({
						url: this.titleDetailNode?.content.posterUrl ?? '',
						imageType: 'poster_detail',
					}),
				},
				{ vmid: 'og:image:width', property: 'og:image:width', content: '718' },
				{ vmid: 'og:image:height', property: 'og:image:height', content: '1024' },
			],
		};
	}

	get queryVariables(): GetUrlTitleDetailsQueryVariables {
		let fullPath = this.activeRoute?.fullPath;

		// GraphQL query for TitleDetail doesn't support URL query params like `?serve_fallback=true`
		// so we have to remove them before making a request
		if (fullPath.includes('?')) {
			fullPath = fullPath.split('?')[0];
		}

		function decodePath(path: string) {
			return path
				.split(':')
				.map(part => decodeURI(part))
				.join(':');
		}

		return {
			fullPath: decodePath(fullPath),
			language: this.language,
			country: this.country,
			episodeMaxLimit: this.episodeMaxLimit,
			platform: Platform.Web,
			allowSponsoredRecommendations: {
				country: this.country,
				platform: Platform.Web,
				pageType: 'VIEW_TITLE_DETAIL',
				language: this.language,
			},
		};
	}

	get preventCLS() {
		return process.client && !this.isReadyToRender;
	}

	/**
	 * Inject lowPriorityCallback from App.vue
	 */
	@Inject({ default: () => {} }) disableScroll: any;
	@Inject({ default: () => {} }) enableScroll: any;

	@Watch('preventCLS', { immediate: true })
	onPreventCLS(value: boolean) {
		if (value) {
			this.disableScroll();
		} else {
			this.enableScroll();
		}
	}

	get hasTrailers() {
		return !!this.titleContent.clips?.length;
	}

	get hasBundleOffers() {
		if (this.titleDetailNode?.promotedBundles) {
			return this.titleDetailNode?.promotedBundles?.length > 0;
		}

		return false;
	}

	get additionalContexts() {
		const refferedModuleContext: SnowplowModuleContext | null = this.refferalModuleContext;

		const contexts: SnowplowContext[] = [];
		if (refferedModuleContext) contexts.push(refferedModuleContext);

		return contexts;
	}

	get metaData() {
		const { ...rest } = this.titleDetails;

		return {
			...rest,
			htmlContent: JSON.parse(rest.htmlContent) || {},
		};
	}

	get title() {
		return this.isSeason ? this.titleDetailNode.show! : this.titleDetailNode;
	}

	get titleName() {
		return this.isSeason ? this.titleDetailNode.show?.content.title : this.titleContent.title;
	}

	get titleId() {
		if (this.isSeason) {
			return this.titleDetailNode.show?.id;
		}
		return this.titleDetailNode.id;
	}

	get titleObjectId() {
		return this.isSeason ? this.titleDetailNode.show!.objectId : this.titleDetailNode.objectId;
	}

	get titleObjectType() {
		return this.isSeason ? this.titleDetailNode.show!.objectType : this.titleDetailNode.objectType;
	}

	get titlePosterUrl() {
		return this.isSeason ? this.titleDetailNode.show!.content.posterUrl : this.titleContent.posterUrl;
	}

	get titleBackdrops() {
		return this.isSeason ? this.titleDetailNode.show!.content.backdrops : this.titleContent.backdrops;
	}

	get seasonName() {
		return this.isSeason ? this.titleContent.title : null;
	}

	/* SAFARI RENDERING ISSUE FIX */
	//purposed added variable for the safari fix
	get isDesktopSafari() {
		if (process.server) {
			return false;
		}
		const ua = navigator.userAgent;
		const safariDesktop = /(Macintosh).*AppleWebKit.*Safari/;
		const excludedBrowsers = /Chrome|Firefox|CriOS|OPiOS|OPiOS|mercury|FxiOS/;
		return safariDesktop.test(ua) && !excludedBrowsers.test(ua);
	}

	handleBuyboxLoading(isLoading: boolean) {
		this.isReadyToRender = !isLoading;
	}

	get titleContent(): TitleDetailsNode['content'] {
		return this.titleDetailNode?.content ?? {};
	}

	get hasBackdropContent(): boolean {
		const hasCarouselImages = this.titleContent.backdrops?.length > 0;
		const hasTrailerClips = this.backdropClips.length > 0;

		return hasCarouselImages || hasTrailerClips;
	}

	get backdropClips() {
		return TitleHelper.getBackdropClips(this.titleDetails.node);
	}

	// is the title detail page in loading mode for:
	// - requests
	// - async components loading (note: if we don't wait for async component loading, the CLS will increase)
	get isLoading() {
		return this.isDataLoading || !this.isReadyToRender;
	}

	// is the title detail page in loading mode for:
	// - requests
	get isDataLoading() {
		return this.$apollo.queries.titleDetails.loading;
	}

	get isSeoTraffic() {
		return TrafficHelper.isSeoTraffic();
	}

	get shouldRenderOnServer() {
		const { isSeoUser, isTitleUpcoming } = this;
		return isSeoUser && !isTitleUpcoming;
	}

	get showAdUnit(): boolean {
		return showFreemium() && !this.metaData.htmlContent.exclude_from_ads;
	}

	get showCustomSynopsis() {
		return this.metaData.htmlContent?.detail_heading != null && this.metaData?.htmlContent?.detail_content != null;
	}

	get showSynopsis(): boolean {
		return !this.showCustomSynopsis && !!this.titleContent.shortDescription;
	}

	get seasonEpisodesAvailable() {
		return this.isSeason && this.titleDetailNode?.episodes?.length > 0;
	}

	get isShow() {
		return this.titleDetailNode?.objectType === ObjectType.Show;
	}

	get isSeason() {
		return this.titleDetailNode.objectType === ObjectType.ShowSeason && this.titleDetailNode.show != null;
	}

	get showSeasons() {
		if (this.isShow) {
			return this.titleDetailNode?.seasons || [];
		}
		return [];
	}

	get showSeasonsAvailable() {
		return this.showSeasons.length > 0;
	}

	get recentEpisodesAvailable() {
		return this.titleDetailNode?.recentEpisodes ? this.titleDetailNode.recentEpisodes.length > 0 : false;
	}

	get ageCertification() {
		const cert = this.isSeason
			? this.titleDetailNode.show?.content.ageCertification
			: this.titleContent.ageCertification;
		// if cert is an empty string it should be considered as absent
		return cert || null;
	}

	get credits() {
		return this.isSeason ? this.titleDetailNode.show?.content.credits : this.titleContent.credits;
	}

	get titleFullPath() {
		return this.isSeason ? this.titleDetailNode.show?.content.fullPath : this.titleContent.fullPath;
	}

	get isTitleUpcoming() {
		return !this.titleContent.isReleased;
	}

	get showSimilarTitles() {
		return this.titleDetailNode && !this.isTitleUpcoming && !this.metaData.htmlContent.hide_similar_titles;
	}

	get titleDetailNode(): TitleDetailsNode {
		return this.titleDetails.node;
	}

	get curatedUpcomingHeadline() {
		switch (this.titleDetailNode.objectType) {
			case ObjectType.Movie:
				return this.$t('WEBAPP_UPCOMING_MOVIES_LIST');
			case ObjectType.Show:
			case ObjectType.ShowSeason:
				return this.$t('WEBAPP_UPCOMING_SHOWS_LIST');
		}
	}

	get firstTitleGenreShortName() {
		return this.titleContent.genres?.[0]?.shortName ?? null;
	}

	get similarUpcomingHeadline() {
		if (!this.firstTitleGenreShortName) return this.$t('WEBAPP_UPCOMING_TITLES_LIST');

		const genre = this.genresByShortName[this.firstTitleGenreShortName].translation;
		switch (this.titleObjectType) {
			case ObjectType.Movie:
				return this.$t('WEBAPP_UPCOMING_GENRE_MOVIES_LIST', { genre });
			case ObjectType.Show:
				return this.$t('WEBAPP_UPCOMING_GENRE_SHOWS_LIST', { genre });
		}
	}

	get similarUpcomingFilters(): TitleFilter {
		return {
			genres: [this.firstTitleGenreShortName].filter(f => f),
			objectTypes: [this.titleObjectType],
		};
	}

	get customTitleListID() {
		if (JW_CONFIG.DOMAIN.includes('moviecycle')) {
			return 'tl-tu-7c7fce97-990c-4bdd-b3b1-2e3bf29a3fb6';
		}

		return this.titleObjectType === ObjectType.Movie
			? 'tl-tu-92861397-5f87-4233-ab53-cd06b7a97294'
			: 'tl-tu-166ee44c-3f7c-4f61-8650-13baedc7b4b8';
	}

	get customTitleListTitle(): PopularTitle {
		return {
			id: this.titleObjectId,
			object_type: this.titleObjectType.toLowerCase(),
			title: this.titleName,
			jw_entity_id: this.titleId,
		} as PopularTitle;
	}

	get quickActionBarProps(): QuickActionBarProps {
		const props: QuickActionBarProps = {
			titleId: this.titleDetailNode.id,
			titleName: this.titleContent.title,
			titleObjectId: this.titleDetailNode.objectId,
			titleObjectType: this.titleDetailNode.objectType,
			titlePosterUrl: this.titleContent.posterUrl ?? '',
			titleBackdrops: this.titleContent.backdrops,
			titleContentType: this.titleContentType,
			titleSeenEpisodeCount: (this.titleDetailNode.seenState || {}).seenEpisodeCount,
			isInWatchlist: this.isTitleInList(TitleListType.WatchNext),
			isInSeenList: this.isTitleInList(TitleListType.CaughtUp),
			isInLikeList: this.isTitleInList(TitleListType.Likelist),
			isInDislikeList: this.isTitleInList(TitleListType.Dislikelist),
			isTitleUpcoming: this.isTitleUpcoming,
			isTitleReleased: this.titleContent.isReleased,
			seasonNumber: this.titleContent.seasonNumber,
			without: [TitleListName.SEENLIST],
			fullWidth: true,
			additionalContexts: this.additionalContexts,
		};

		if (this.isSeason && this.showDetails) {
			props.showDetails = this.showDetails;
		}

		return props;
	}

	// Required for quick actions, when we want to toggle status of the show on season page
	get showDetails() {
		if (this.titleDetailNode.show) {
			return {
				id: this.titleDetailNode.show.id,
				objectType: this.titleDetailNode.show.objectType,
				objectId: this.titleDetailNode.show.objectId,
				title: this.titleDetailNode.show.content.title,
				isReleased: this.titleDetailNode.show.content.isReleased,
			};
		}

		return null;
	}

	get hidePromotedBuyBox() {
		return this.metaData.htmlContent.hide_sponsored_buybox === true;
	}

	@Watch('isLoggedIn')
	onLoggedInChange() {
		this.$apollo.queries.titleDetails.refetch();
	}

	@Watch('activeRoute')
	async onRouteChange(route: Route) {
		//refresh page if the browser is safari and if the route title name is unique
		if (
			this.isDesktopSafari &&
			this.isShow &&
			(this.lastRoute.name || '').indexOf('app.detail.show') === 0 &&
			this.lastRoute.params.title !== this.activeRoute.params.title
		) {
			this.$router.go(0);
		}

		const titleHasChanged = (route.name || '').includes('app.detail') && this.titleContent?.fullPath !== route.path;

		if (titleHasChanged) {
			this.scrollToTop();
			// We need to reset title object, to prevent CLS issue;
			// @ts-ignore
			this.titleDetails = {};
			this.clearTitleContext();
			this.setEpisodeMaxLimit(20);
		}

		if (!route.name?.includes('app.detail')) {
			resetPermanentAudiences();
			this.setSponsoredRecommendation(null);
		}
	}

	@Watch('titleDetails')
	onTitleDetailChange(newTitleDetails: TitleDetails, oldTitleDetails: TitleDetails) {
		const isNewTitle = newTitleDetails?.node && !oldTitleDetails?.node;
		if (isNewTitle) {
			if (newTitleDetails?.node?.id) {
				const payload: SnowplowTitleContextGraphqlPayload = [
					this.titleObjectId,
					this.titleObjectType,
					this.titleContent.seasonNumber || undefined,
					undefined,
					this.titleContentType,
				];

				this.setTitleContext(payload);
				this.$store.dispatch('meta/setTitleJsonLD', { title: newTitleDetails.node });
			}

			TrackingHelper.trackPage(this.$route.path, this.additionalContexts);
		}
	}

	get isShowAvailabilityBadge() {
		return this.isLoggedIn() && !!this.availabilityBadgeDate;
	}

	get titleContentType() {
		if (this.isShowAvailabilityBadge) {
			return this.availabilityBadgeType === AvailabilityBadgeType.Upcoming
				? TitleContentType.ComingSoon
				: TitleContentType.LeavingSoon;
		}

		return undefined;
	}

	get upcomingReleaseFromSelectedPackages() {
		const userPackages = this.settings.providers;

		return (
			this.titleContent.upcomingReleases
				?.filter(item => diffInMilliseconds(new Date(item.releaseDate)) > 0)
				.filter(item => (item?.releaseCountDown ?? diffInDays(new Date(item.releaseDate))) < 30)
				.find(item => userPackages.includes(item.package?.shortName)) || null
		);
	}

	get leavingOfferFromSelectedPackages() {
		const userPackages = this.settings.providers;
		return (
			this.titleDetailNode.availableTo
				?.filter(item => (item?.availableCountDown ?? diffInDays(new Date(item.availableToDate))) < 30)
				.find(item => userPackages.includes(item.package?.shortName)) || null
		);
	}

	get availabilityBadgeType() {
		return !!this.upcomingReleaseFromSelectedPackages
			? AvailabilityBadgeType.Upcoming
			: AvailabilityBadgeType.Leaving;
	}

	get availabilityBadgeDate() {
		if (!!this.upcomingReleaseFromSelectedPackages) {
			return this.upcomingReleaseFromSelectedPackages.releaseDate;
		}

		if (!!this.leavingOfferFromSelectedPackages) {
			return this.leavingOfferFromSelectedPackages.availableToDate;
		}

		return null;
	}

	get availabilityBadgeCountdown() {
		if (!!this.upcomingReleaseFromSelectedPackages) {
			return this.upcomingReleaseFromSelectedPackages.releaseCountDown;
		}

		if (!!this.leavingOfferFromSelectedPackages) {
			return this.leavingOfferFromSelectedPackages.availableCountDown;
		}

		return null;
	}

	get sponsoredRecommendationPromotion() {
		return 'NONE';
	}

	get isSponsoredRecommendationCohort() {
		return this.title.similarTitlesV2?.sponsoredAd?.holdoutGroup;
	}

	get promoteSponsoredRecommendation() {
		return (
			this.sponsoredAd &&
			this.isSponsoredRecommendationCohort &&
			!this.isPremium &&
			this.sponsoredRecommendationPromotion !== 'NONE'
		);
	}

	isTitleInList(listName: TitleListType) {
		switch (listName) {
			case TitleListType.WatchNext:
				if (this.isSeason) {
					return !!this.titleDetailNode.show?.watchlistEntry?.createdAt;
				}
				return !!this.titleDetailNode?.watchlistEntry?.createdAt;
			case TitleListType.Likelist:
				if (this.isSeason) {
					return !!this.titleDetailNode.show?.likelistEntry?.createdAt;
				}
				return !!this.titleDetailNode?.likelistEntry?.createdAt;
			case TitleListType.Dislikelist:
				if (this.isSeason) {
					return !!this.titleDetailNode.show?.dislikelistEntry?.createdAt;
				}
				return !!this.titleDetailNode?.dislikelistEntry?.createdAt;
			case TitleListType.CaughtUp:
				return this.titleDetailNode.objectType === ObjectType.Movie
					? !!this.titleDetailNode?.seenlistEntry?.createdAt
					: this.titleDetailNode?.seenState?.progress === 100;
		}
	}

	trackSeasonTitleClicked({ index, title }: { index: number; title: TitleDetailsNode }) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{ action: 'title_clicked', label: `${title.content.seasonNumber}`, property: 'season', value: `${index}` },
			[
				new SnowplowTitleContextGraphql(title.objectId, ObjectType.ShowSeason, title.content.seasonNumber),
				...(this.refferalModuleContext ? [this.refferalModuleContext] : []),
			]
		);
	}

	showErrorToast() {
		// ToastHelper.error({ message: this.$t('WEBAPP_ALERT_ERROR_TITLE') as string, duration: 2000 });
	}

	setEpisodeMaxLimit(value: number | null) {
		this.episodeMaxLimit = value;
	}

	scrollToTop() {
		this.content?.scrollToTop();
	}
}
